import React, { forwardRef } from 'react';
import { ICustomInput } from '../../types/input';
import { DatePicker, TimePicker } from '../dateTimePicker/DateTimePicker';
import Typography from '../typography/Typography';
import './input.scss';
import SearchIcon from '../icons/SearchIcon';
import CheckIcon from '../icons/CheckIcon';
import DangerIcon from '../icons/DangerIcon';
import { XIcon } from '../icons';
import Loader from '../loader/Loader';

const Input = forwardRef<HTMLInputElement, ICustomInput>(
  (
    {
      name,
      onChange,
      onBlur,
      onWheel,
      onFocus,
      clear,
      hasValue,
      error = '',
      className = '',
      label,
      defaultValue = undefined,
      disabled = false,
      placeholder = '',
      type = 'text',
      nonEditable = false,
      search = false,
      confirmed = false,
      iconLeft,
      fullWidth = false,
      upperCase = false,
      clearButtonShow = false,
      loading = false,
      maxDate,
      ...props
    },
    ref,
  ) => {
    const classBase = 'custom-input';

    let classes = classBase;

    if (disabled) classes += ` ${classBase}-disabled`;
    if (nonEditable) classes += ` ${classBase}-non-editable`;
    if (hasValue) classes += ` ${classBase}-has-value`;
    if (search) classes += ` ${classBase}-search`;
    if (error) classes += ` ${classBase}-error`;
    if (!error && confirmed) classes += ` ${classBase}-success`;
    if (iconLeft) classes += ` ${classBase}-icon-left`;
    if (upperCase) classes += ' uppercase';

    const clearText = () => {
      const input = document.getElementsByName(name!)[0];
      if (input instanceof HTMLInputElement) {
        input.value = '';
        if (clear) clear();
      }
    };
    // Do not change the order of the elements, adjancent element selector is used for styling
    return (
      <div className={`custom-input-wrapper ${className} ${fullWidth ? 'width-12' : ''} ${nonEditable ? 'non-editable' : ''}`}>
        {/* eslint-disable */}
        {type === 'custom-date'
          ? (
            <DatePicker
              name={name}
              onChange={onChange}
              classes={`${classes} f-s-16 width-12 default-font`}
              disabled={disabled || nonEditable}
              ref={ref}
              defaultValue={defaultValue || undefined}
              dataTestId={classBase}
              maxDate={maxDate}
              {...props}
            />
          )
          : type === 'custom-time'
            ? (
              <TimePicker
                name={name}
                onChange={onChange}
                classes={`${classes} f-s-16 width-12 default-font`}
                disabled={disabled || nonEditable}
                ref={ref}
                defaultValue={defaultValue || undefined}
                dataTestId={classBase}
                {...props}
              />
            )
            : (
              <input
                name={name}
                onChange={onChange}
                onBlur={onBlur}
                onWheel={onWheel}
                onFocus={onFocus}
                placeholder={placeholder}
                className={`${classes} f-s-16 width-12 default-font`}
                type={type}
                disabled={disabled || nonEditable}
                ref={ref}
                value={defaultValue || undefined}
                data-testid={classBase}
                {...props}
              />
            )}
        {/* eslint-enable */}
        <Typography variant="caption">{label || ''}</Typography>
        {error && <DangerIcon color="red5" className="input-status-icon" height={20} />}
        {confirmed && !error && <CheckIcon color="atlantis5" className="input-status-icon" height={12} />}
        {search && <SearchIcon color="asphalt5" className="search-icon" height={19} />}
        {(search && clearButtonShow) && <span onClick={clearText} className="clear-buton-icon pointer"><XIcon color="asphalt7" width={15} /></span>}
        {iconLeft && <span className="icon-left-container">{iconLeft}</span>}
        {loading && <span className="loader-right-container"><Loader spinner /></span>}
        {error && (
        <Typography className="custom-input-error-message" variant="error">
          {error}
        </Typography>
        )}
      </div>
    );
  },
);

export default Input;

Input.displayName = 'Input';
